import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { GeocodeSuggestion } from '../services/geocoding';
import AddressMapSearch from '../components/AddressMapSearch';
import SimpleAddressInput, { SimpleAddressInputRef, SimpleAddressData } from '../components/SimpleAddressInput';
import './Auth.css';

type AddressMode = 'simple' | 'advanced';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useLanguage();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
  });

  // RGPD consent state
  const [rgpdConsent, setRgpdConsent] = useState({
    privacyPolicy: false,
    locationSharing: false,
  });

  // Address mode state
  const [addressMode, setAddressMode] = useState<AddressMode>('simple');

  // Separate state for address (will be set by AddressMapSearch or SimpleAddressInput)
  const [addressData, setAddressData] = useState<GeocodeSuggestion | SimpleAddressData | null>(null);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Ref for SimpleAddressInput to trigger validation
  const simpleAddressRef = useRef<SimpleAddressInputRef>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.register.errors.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.register.errors.passwordLength'));
      return;
    }

    // RGPD consent validation
    if (!rgpdConsent.privacyPolicy) {
      setError(t('auth.register.consent.privacyRequired'));
      return;
    }

    if (!rgpdConsent.locationSharing) {
      setError(t('auth.register.consent.locationRequired'));
      return;
    }

    // For simple mode, trigger validation
    if (addressMode === 'simple') {
      if (!simpleAddressRef.current) {
        setError(t('auth.register.errors.addressFormError'));
        return;
      }

      setLoading(true);

      // Validate address via geocoding
      const isValid = await simpleAddressRef.current.validateAddress();

      if (!isValid) {
        setLoading(false);
        // Error message is already set by SimpleAddressInput
        return;
      }

      // Address data will be set via onAddressValidate callback
      // Continue with submission after addressData is set
    }

    // Validate address is selected (for both modes)
    if (!addressData) {
      setError(t('auth.register.errors.addressRequired'));
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Combine user data with address data
      const registerData = {
        ...formData,
        street_number: (addressData as any).street_number || undefined,
        street_name: (addressData as any).street_name || undefined,
        street_address: addressData.street_address,
        city: addressData.city,
        district: (addressData as any).district || undefined,
        state: addressData.state || undefined,
        postal_code: addressData.postal_code || undefined,
        country: addressData.country,
        latitude: addressData.latitude,
        longitude: addressData.longitude,
      };

      const { confirmPassword, ...dataToSend } = registerData;

      // Set flag to prevent PublicRoute from redirecting to /map
      sessionStorage.setItem('justRegistered', 'true');

      await register(dataToSend);
      navigate('/address-privacy-setup', { replace: true });
    } catch (err: any) {
      // Show detailed error message including validation details
      const errorData = err.response?.data;
      let errorMessage = errorData?.error || t('auth.register.errors.registrationFailed');

      // If there are validation details, just show the message without field name
      if (errorData?.details && errorData.details.length > 0) {
        errorMessage = errorData.details[0].message; // Show only the first error message
      } else if (errorData?.fields && errorData.fields.length > 0) {
        // Fallback for old format (missing fields)
        errorMessage = `${errorMessage}: ${errorData.fields.join(', ')}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSelect = (address: GeocodeSuggestion) => {
    setAddressData(address);
    setError(''); // Clear any address-related errors
  };

  const handleSimpleAddressValidate = (data: SimpleAddressData) => {
    setAddressData(data);
    setError(''); // Clear any address-related errors
  };

  const handleSwitchToAdvanced = () => {
    setAddressMode('advanced');
    // Scroll to address section after mode change
    setTimeout(() => {
      document.getElementById('address-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSwitchToSimple = () => {
    setAddressMode('simple');
    // Clear address data when switching back
    setAddressData(null);
    // Scroll to address section after mode change
    setTimeout(() => {
      document.getElementById('address-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="auth-container">
      <div className="auth-card register-card">
        <h1>{t('auth.register.title')}</h1>
        <p className="auth-subtitle">{t('auth.register.subtitle')}</p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">{t('auth.register.firstName')} {t('auth.register.required')}</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">{t('auth.register.lastName')} {t('auth.register.required')}</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('auth.register.email')} {t('auth.register.required')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">{t('auth.register.phone')}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              autoComplete="tel"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">{t('auth.register.password')} {t('auth.register.required')}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.register.confirmPassword')} {t('auth.register.required')}</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                minLength={6}
                style={formData.confirmPassword && formData.password !== formData.confirmPassword ? {
                  borderColor: 'var(--danger, #dc3545)',
                  boxShadow: '0 0 0 2px rgba(220, 53, 69, 0.2)'
                } : undefined}
              />
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <span className="field-error" style={{
                  color: 'var(--danger, #dc3545)',
                  fontSize: '12px',
                  marginTop: '4px',
                  display: 'block'
                }}>
                  {t('auth.register.errors.passwordMismatch')}
                </span>
              )}
              {formData.confirmPassword && formData.password === formData.confirmPassword && formData.confirmPassword.length >= 6 && (
                <span className="field-success" style={{
                  color: 'var(--accent-primary, #00aa9f)',
                  fontSize: '12px',
                  marginTop: '4px',
                  display: 'block'
                }}>
                  ✓ {t('auth.register.passwordMatch') || 'Mots de passe identiques'}
                </span>
              )}
            </div>
          </div>

          <h3 id="address-section" className="section-title">📍 {t('auth.register.addressTitle')}</h3>

          {/* Instructions for advanced mode */}
          {addressMode === 'advanced' && (
            <>
              <p className="key-instruction" style={{
                marginBottom: '12px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--accent-primary, #00aa9f)',
                padding: '12px 16px',
                backgroundColor: 'var(--accent-light, #e8f5f4)',
                borderRadius: '8px',
                borderLeft: '4px solid var(--accent-primary, #00aa9f)',
                textAlign: 'center'
              }}>
                {t('auth.register.advancedMode.mainInstruction')}
              </p>
              <div className="help-text" style={{ marginBottom: '12px' }}>
                <strong>{t('auth.register.advancedMode.tipsTitle')}</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>{t('auth.register.advancedMode.tip1')}</li>
                  <li>{t('auth.register.advancedMode.tip2')}</li>
                  <li>{t('auth.register.advancedMode.tip3')}</li>
                </ul>
              </div>
              <p className="section-subtitle" style={{ fontStyle: 'italic', marginBottom: '15px' }}>
                {t('auth.register.addressSubtitle')}
              </p>
            </>
          )}

          {/* Subtitle for simple mode */}
          {addressMode === 'simple' && (
            <p className="section-subtitle">{t('auth.register.addressSubtitle')}</p>
          )}

          {/* Address input - Simple or Advanced mode */}
          {addressMode === 'simple' ? (
            <SimpleAddressInput
              ref={simpleAddressRef}
              onAddressValidate={handleSimpleAddressValidate}
              onSwitchToAdvanced={handleSwitchToAdvanced}
              showAdvancedToggle={true}
            />
          ) : (
            <>
              <AddressMapSearch onAddressSelect={handleAddressSelect} mode="high-quality" />
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                  type="button"
                  className="btn-toggle-simple"
                  onClick={handleSwitchToSimple}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary, #888)',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    opacity: 0.7,
                  }}
                >
                  {t('auth.register.backToSimple')}
                </button>
              </div>
            </>
          )}

          {/* RGPD Consent Checkboxes */}
          <div className="rgpd-consent-section" style={{ marginTop: '24px', padding: '20px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '16px', marginBottom: '16px', color: 'var(--text-primary)' }}>📋 {t('auth.register.consentTitle')}</h3>

            <div className="consent-checkbox" style={{ marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={rgpdConsent.privacyPolicy}
                  onChange={(e) => setRgpdConsent({ ...rgpdConsent, privacyPolicy: e.target.checked })}
                  style={{ marginRight: '12px', marginTop: '4px', cursor: 'pointer' }}
                  required
                />
                <span style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--text-primary)' }}>
                  {t('auth.register.consent.privacy').split('<link>')[0]}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    style={{ color: 'var(--accent-primary)', textDecoration: 'underline' }}
                  >
                    {t('settings.privacyPolicy')}
                  </Link>
                  {t('auth.register.consent.privacy').split('</link>')[1] || ''}{' '}
                  <span style={{ color: 'var(--danger)' }}>*</span>
                </span>
              </label>
            </div>

            <div className="consent-checkbox">
              <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={rgpdConsent.locationSharing}
                  onChange={(e) => setRgpdConsent({ ...rgpdConsent, locationSharing: e.target.checked })}
                  style={{ marginRight: '12px', marginTop: '4px', cursor: 'pointer' }}
                  required
                />
                <span style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--text-primary)' }}>
                  {t('auth.register.consent.location')}{' '}
                  <span style={{ color: 'var(--danger)' }}>*</span>
                </span>
              </label>
            </div>

            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '12px', fontStyle: 'italic' }}>
              {t('auth.register.consent.withdrawNote')}
            </p>
          </div>

          {/* Error message displayed above the submit button */}
          {error && (
            <div className="error-message" style={{ marginTop: '20px', marginBottom: '15px', position: 'relative', zIndex: 1000 }}>
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary" disabled={loading} style={{ marginTop: error ? '0' : '20px' }}>
            {loading ? t('auth.register.creating') : t('auth.register.submit')}
          </button>
        </form>

        <p className="auth-footer">
          {t('auth.register.footer')} <Link to="/login">{t('auth.register.footerLink')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
